/**
 * BIZZMATIX ComDesk inside the back office (ComDesk SEAM §5).
 *
 * The HealthPass layout keeps its header and menu; ComDesk fills the content
 * area in one frame of `/backoffice/comdesk/?embed=1#<surface>`. Same origin,
 * so ComDesk signs in through the HealthPass session (/api/oidc) without a
 * page of its own, and draws neither its rail nor a sign-out.
 *
 * Both ways by postMessage, this origin only:
 *   ComDesk → here  { comdesk: 'route', hash }    the URL and the menu follow
 *   here → ComDesk  { comdesk: 'navigate', hash } a menu item while it is open
 *   ComDesk → here  { bizzmatix: 'ready', v: 1 }  answered with the theme
 *   here → ComDesk  { bizzmatix: 'theme', v: 1, scheme, tokens }
 *
 * The theme is read from the app's own tokens at the moment it is sent, so
 * ComDesk looks like HealthPass in light and dark alike, with no settings.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { COMDESK_BASE } from "@/hooks/useComDeskMenu";
import { hashFor, hostTheme, pathFor } from "@/utils/comdeskFrame";

/** What ComDesk's frame may use: its manifest's `allow` list. */
const ALLOW =
  "microphone; camera; display-capture; autoplay; clipboard-write; fullscreen";

export default function ComDeskFrame() {
  const frame = useRef<HTMLIFrameElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  // The frame loads once, at the surface it was opened on. Later moves are
  // messages, so ComDesk keeps its state instead of reloading.
  const initial = useRef(hashFor(location.pathname));
  // The hash ComDesk last reported, so its own moves are not sent back to it.
  const reported = useRef(initial.current);

  useEffect(() => {
    const origin = window.location.origin;
    const post = (message: object) =>
      frame.current?.contentWindow?.postMessage(message, origin);
    const sendTheme = () => post({ bizzmatix: "theme", v: 1, ...hostTheme() });

    const onMessage = (event: MessageEvent) => {
      if (
        event.origin !== origin ||
        event.source !== frame.current?.contentWindow
      ) {
        return;
      }
      const data = event.data as {
        comdesk?: unknown;
        bizzmatix?: unknown;
        hash?: unknown;
      };
      if (data?.bizzmatix === "ready") sendTheme();
      if (data?.comdesk === "route") {
        const path = pathFor(data.hash);
        if (!path) return;
        reported.current = data.hash as string;
        if (path !== window.location.pathname)
          navigate(path, { replace: true });
      }
    };
    window.addEventListener("message", onMessage);

    // Light/dark is a class on <html> (stores/themeStore.ts): follow it live.
    const observer = new MutationObserver(sendTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      window.removeEventListener("message", onMessage);
      observer.disconnect();
    };
  }, [navigate]);

  // A menu item chosen while ComDesk is open.
  useEffect(() => {
    const hash = hashFor(location.pathname);
    if (hash === reported.current) return;
    reported.current = hash;
    frame.current?.contentWindow?.postMessage(
      { comdesk: "navigate", hash },
      window.location.origin,
    );
  }, [location.pathname]);

  return (
    <iframe
      ref={frame}
      title="ComDesk"
      src={`${COMDESK_BASE}/?embed=1${initial.current}`}
      allow={ALLOW}
      className="block w-full border-0 h-[calc(100vh-4rem)]"
    />
  );
}
